import type React from 'react'
import {useState, useRef, useCallback} from 'react'
import {Popover, Icon} from '@pepsico-ds/ui'
import {useLocation} from 'react-router-dom'
import {useSelector} from 'react-redux'
import type {RootState} from '@/store'
import {getCurrentUserEmail} from '@/utils/userHelper'
import {getSectionName} from '@/components/FullStory'
import {SERVICENOW_BASE_URL} from '@/config/envConfig'
import {color} from '@/theme/Color'

// --- Constants ---

/** Assignment group sys_id for the Engineering Maturity Dashboard support group */
const ASSIGNMENT_GROUP_SYS_ID = 'eec5303587d3a9d0c532cae7cebb35a8'
const SHORT_DESCRIPTION = 'DevLens - Issue Report'

/** Whether the ServiceNow base URL is configured (not empty / unresolved template) */
const isServiceNowConfigured = (): boolean => !!SERVICENOW_BASE_URL && !SERVICENOW_BASE_URL.startsWith('${')

/** Theme-aware popover colors (inline styles required because the popover
 *  is portaled into the GDS header DOM where CSS variable resolution fails) */
const POPOVER_THEME = {
  light: {
    background: color.lightBackground,
    text: color.lightText,
    border: color._E0E0E0,
  },
  dark: {
    background: color.darkBackground,
    text: color.darkText,
    border: color._484848,
  },
} as const

// --- Helpers ---

export interface IssueContext {
  email?: string | null
  page?: string
  teamName?: string
  programName?: string
}

/**
 * Builds the ServiceNow SCTASK creation URL with pre-populated fields:
 * - assignment_group: EMD support group
 * - short_description: "DevLens - Issue Report"
 * - description: user context (who, page, program/team)
 */
export const buildIssueUrl = (baseUrl: string, context: IssueContext): string => {
  const descriptionParts: string[] = []
  if (context.email) descriptionParts.push(`Reported by: ${context.email}`)
  if (context.page) descriptionParts.push(`Page: ${context.page}`)
  if (context.programName) descriptionParts.push(`Program: ${context.programName}`)
  if (context.teamName) descriptionParts.push(`Team: ${context.teamName}`)

  const queryParts: string[] = [`assignment_group=${ASSIGNMENT_GROUP_SYS_ID}`, `short_description=${SHORT_DESCRIPTION}`]

  if (descriptionParts.length > 0) {
    queryParts.push(`description=${descriptionParts.join(' | ')}`)
  }

  // ServiceNow uses ^ as AND separator in sysparm_query
  const encodedQuery = encodeURIComponent(queryParts.join('^'))
  return `${baseUrl}/sc_task.do?sys_id=-1&sysparm_query=${encodedQuery}`
}

/** Returns inline styles for the popover dropdown, themed for light/dark. */
const getPopoverStyle = (isDark: boolean): React.CSSProperties => {
  const palette = isDark ? POPOVER_THEME.dark : POPOVER_THEME.light
  return {
    position: 'absolute',
    top: '100%',
    bottom: 'auto',
    right: 0,
    left: 'auto',
    transform: 'none',
    zIndex: 999,
    marginTop: '4px',
    background: palette.background,
    color: palette.text,
    border: `1px solid ${palette.border}`,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
  }
}

// --- Hooks ---

/** Gathers user/page/team/program context for pre-populating the ServiceNow form. */
const useIssueContext = (): IssueContext => {
  const location = useLocation()
  const userEmail = getCurrentUserEmail()
  const sectionName = getSectionName(location.pathname)

  const selectedTeamId = useSelector((state: RootState) => state.team?.selectedTeamId)
  const teams = useSelector((state: RootState) => state.team?.teams ?? [])
  const selectedTeam = teams.find(t => t.dashboardId === selectedTeamId)

  const programs = useSelector((state: RootState) => state.program?.programs ?? [])
  const programName = selectedTeam?.productDashboardId
    ? programs.find(p => p.id === selectedTeam.productDashboardId)?.name
    : undefined

  return {email: userEmail, page: sectionName, teamName: selectedTeam?.name, programName}
}

// --- Sub-components ---

const ReportIssueRow = ({disabled, onClick}: {disabled: boolean; onClick: () => void}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    }}
    className={`help-support-item${disabled ? 'help-support-item--disabled' : ''}`}
    data-testid="help-support-report-issue"
    aria-disabled={disabled}
  >
    <div className="help-support-item__content">
      <span className="help-support-item__title">Report an issue</span>
      <span className="help-support-item__subtitle">Raise or track issues in ServiceNow.</span>
    </div>
    <Icon icon="open_in_new" size="small" />
  </div>
)

// --- Main component ---

const HelpSupportPopover = () => {
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const isDark = useSelector((state: RootState) => state.theme?.mode) === 'dark'
  const context = useIssueContext()
  const hasServiceNowUrl = isServiceNowConfigured()

  const handleReportIssue = useCallback(() => {
    if (!hasServiceNowUrl) return
    window.open(buildIssueUrl(SERVICENOW_BASE_URL, context), '_blank', 'noopener,noreferrer')
    setOpen(false)
  }, [hasServiceNowUrl, context])

  return (
    <div ref={popoverRef} className="help-support-trigger" data-testid="help-support-trigger">
      <button
        onClick={() => setOpen(prev => !prev)}
        aria-label="Help & Support"
        className="help-support-trigger__button"
        data-testid="help-support-button"
      >
        <Icon icon="help_outline" size="medium" />
      </button>

      {open && (
        <Popover
          showPopover={open}
          onClosePopover={() => setOpen(false)}
          text="Help & Support"
          direction="bottom-right"
          showArrow={false}
          outsidePopoverRef={popoverRef as React.RefObject<HTMLDivElement>}
          data-testid="help-support-popover"
          className="help-support-popover"
          style={getPopoverStyle(isDark)}
        >
          <ReportIssueRow disabled={!hasServiceNowUrl} onClick={handleReportIssue} />
        </Popover>
      )}
    </div>
  )
}

export default HelpSupportPopover
